<template>
  <div class="flex-custom container">
    <div class="item-merch-3__tab-wrapper">
      <div class="header">
        <p class="kicker">
          Browse Products
        </p>
        <h2 class="title">
          <span>Shop Our </span>
          <span>{{ activeTabName }}</span>
        </h2>
        <p class="description">
          Smart solutions for the workday world.
        </p>
      </div>
      <div class="tabs">
        <template
          v-for="(item, i) in data"
          :key="item.merch_id || i"
        >
          <a
            class="item-merch-3__tab-wrapper__tab"
            :class="{'active': i === selected}"
            href="javascript:;"
            @click="changeTab(i)"
          >
            {{ item.tab_name }}
          </a>
        </template>
      </div>
    </div>

    <div class="item-merch-3__content-wrapper">
      <!-- Loading overlay -->
      <div
        v-if="isLoading"
        class="item-merch-3__loader"
      >
        <div class="item-merch-3__loader__spinner" />
        <p class="item-merch-3__loader__text">
          Loading products...
        </p>
      </div>
      <template
        v-for="(item, i) in data"
        :key="item.merch_id || i"
      >
        <div
          class="item-merch-3__tab-content"
          :class="{'active': i === selected, 'data-loaded': item.merchLoaded}"
        >
          <div v-if="!item.merchLoaded || item.merch">
            <div class="splide">
              <div class="splide__track">
                <ul
                  class="splide__list"
                  v-html="item.merch"
                />
              </div>
            </div>
          </div>
          <div
            v-else
            class="item-merch-3__slider-empty"
          >
            <h5>No Products Available...</h5>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Splide from '@splidejs/splide';

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selected: 0,
      splideInstances: new Map(),
      rrpProcessed: new Set(),
      isLoading: false,
    };
  },
  computed: {
    activeTabName() {
      const activeItem = this.data && this.data[this.selected];
      return activeItem ? activeItem.tab_name : '';
    },
  },
  mounted() {
    this.queryMerch(0);

    this.$nextTick(() => {
      this.initializeSplides();
      this.processRrpText();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.initializeSplides();
      this.processRrpText();
    });
  },
  beforeUnmount() {
    this.splideInstances.forEach((instance) => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    this.splideInstances.clear();
  },
  methods: {
    changeTab(num) {
      this.selected = num;
      this.queryMerch(num);

      if (this.data[num] && this.data[num].merchLoaded) {
        this.$nextTick(() => {
          this.initializeSplides();
          this.processRrpText();
        });
      }
    },
    initializeSplides() {
      const uninitialized = this.$el.querySelectorAll(
        '.item-merch-3__tab-content.data-loaded .splide:not(.splide-initialized)',
      );

      uninitialized.forEach((splideElement) => {
        const splideInstance = new Splide(splideElement, {
          type: 'loop',
          height: '378px',
          perPage: 5,
          perMove: 1,
          gap: 10,
          arrows: false,
          pagination: true,
          autoplay: false,
          breakpoints: {
            1774: {
              perPage: 4,
            },
            1469: {
              perPage: 3,
            },
            1200: {
              perPage: 2,
            },
            768: {
              perPage: 1,
            },
          },
        });

        splideInstance.mount();
        splideElement.classList.add('splide-initialized');
        this.splideInstances.set(splideElement, splideInstance);
      });
    },
    processRrpText() {
      this.$el.querySelectorAll('.rrp span').forEach((rrpSpan) => {
        const spanId = rrpSpan.textContent;
        if (!this.rrpProcessed.has(spanId) && /RRP\s*/i.test(rrpSpan.textContent)) {
          rrpSpan.textContent = rrpSpan.textContent.replace(/RRP\s*/i, '');
          this.rrpProcessed.add(spanId);
        }
      });
    },
    queryMerch(num) {
      if (!this.data[num] || this.data[num].merchLoaded) {
        if (this.data[num]?.merchLoaded) {
          this.isLoading = false;
        }
        return;
      }

      const merchId = this.data[num].merch_id;
      if (!merchId) {
        console.error('Missing merch_id for item at index', num);
        this.isLoading = false;
        return;
      }

      this.isLoading = true;

      $.ajax({
        type: 'post',
        url: '/ajax/merchandising',
        headers: {
          'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
        },
        data: {
          groupMerchandising_ids: [merchId],
        },
        success: (response) => {
          this.handleMerchResponse(response, num, merchId);
        },
        error: (xhr, status, error) => {
          console.error('Error loading merchandise:', error);
          if (this.data[num]) {
            // Prevent infinite retry; parent passes reactive object
            // eslint-disable-next-line vue/no-mutating-props
            this.data[num].merchLoaded = true;
          }
          this.isLoading = false;
        },
      });
    },
    handleMerchResponse(response, index, merchId) {
      if (!response?.data?.itemmerchandisinghtml?.[merchId]) {
        console.error('Invalid response structure');
        this.isLoading = false;
        return;
      }

      const merchItems = response.data.itemmerchandisinghtml[merchId];
      const doc = new DOMParser().parseFromString(merchItems, 'text/html');
      Array.from(doc.body.children).forEach((item) => item.classList.add('splide__slide'));

      // Parent passes reactive object; update is by design (matches reference pattern)
      // eslint-disable-next-line vue/no-mutating-props
      Object.assign(this.data[index], {
        merch: doc.body.innerHTML,
        merchLoaded: true,
      });

      this.$forceUpdate();

      this.$nextTick(() => {
        document.querySelectorAll('.item-merch-3 .item-merch-3__content-wrapper .active script').forEach((script) => {
          const newScript = document.createElement('script');
          newScript.text = script.text;
          document.body.appendChild(newScript);
          document.body.removeChild(newScript);
        });

        this.initializeSplides();
        this.processRrpText();
        if (typeof window.fetchLiveData === 'function') {
          window.fetchLiveData();
        }

        this.isLoading = false;
      });
    },
  },
};
</script>
